import React from "react";
import MessageAttachButton from "./components/MessageAttachButton";
import useController from "./controller";
import useViewController from "./viewController";
import useMobileKeyboardOpen from "../../hooks/useMobileKeyboard";
import { isPerformer } from "../../lib/usersHelper";
import { useTranslation } from "../../locales";
import TipModal from "../../modals/TipModal";
import BaseButton from "../BaseButton";
import BaseIcon from "../BaseIcon";
import BaseTextarea from "../BaseTextarea";
import MessageAttachView from "../MessageAttachView";
import "./style.scss";

interface Props {
  disableAttachments?: boolean;
  onFocus?: (e: React.FocusEvent) => void;
  className?: string;
  maxRows?: number;
  transparentOnBlur?: boolean;
  userIds?: string[];
  threadIds?: string[];
}

function MessageInput({
  userIds,
  threadIds,
  disableAttachments = false,
  onFocus,
  className,
  transparentOnBlur,
  maxRows = 5,
}: Props) {
  const { t } = useTranslation();
  const {
    user,
    value,
    setValue,
    onSendMessage,
    setTextarea,
    attachedFiles,
    addAttachment,
    removeAttachment,
    removeAllAttachments,
  } = useController(userIds, threadIds);
  const { showTipModal, setShowTipModal } = useViewController();
  const { keyboardOpen } = useMobileKeyboardOpen();
  const cls = ["message-input"];
  if (className) cls.push(className);
  if (transparentOnBlur && !keyboardOpen) cls.push("__transparent");

  return (
    <div className={cls.join(" ")} onClick={(e) => e.stopPropagation()}>
      {!disableAttachments ? (
        attachedFiles.filter((file) => "uploadProgress" in file).length > 0 ? (
          <BaseButton icon={true} borderless className="mr-1" name="delete-files" onClick={removeAllAttachments}>
            <BaseIcon>close</BaseIcon>
          </BaseButton>
        ) : (
          <MessageAttachButton className="mr-1 mt-auto message-input__btn" onInput={addAttachment} />
        )
      ) : null}
      <div className="message-input__input-wrapper">
        {attachedFiles.map((attachedFile) => (
          <React.Fragment key={attachedFile.id}>
            <div className="mb-1">
              <MessageAttachView attachedFileData={attachedFile} removeAttachment={removeAttachment} />
            </div>
          </React.Fragment>
        ))}
        <BaseTextarea
          setTextarea={setTextarea}
          className="message-input__input"
          value={value}
          type="textarea"
          onInput={setValue}
          name="message-input"
          placeholder={t("Write your message...")}
          maxRows={maxRows}
          rowHeight={23}
          autoGrow={true}
          rows={1}
          maxLength={3000}
          submit="enter"
          onSubmit={onSendMessage}
          onFocus={onFocus}
        />
      </div>
      {threadIds?.length === 1 && userIds?.length === 1 && !isPerformer(user) && (
        <BaseButton
          className="mt-auto mr-1"
          icon={true}
          borderless={true}
          onClick={() => setShowTipModal(true)}
          name="open-tip"
        >
          <BaseIcon>pay-service</BaseIcon>
        </BaseButton>
      )}
      <BaseButton
        className="mt-auto message-input__btn"
        icon={true}
        borderless={true}
        onClick={onSendMessage}
        name="send-message"
      >
        <BaseIcon>send</BaseIcon>
      </BaseButton>
      {threadIds?.length === 1 && userIds?.length === 1 && !isPerformer(user) && (
        <TipModal
          threadId={threadIds[0]}
          userId={userIds[0]}
          show={showTipModal}
          onClose={() => setShowTipModal(false)}
        />
      )}
    </div>
  );
}

export default MessageInput;
